import type { AiModelsCatalogClient } from "../catalog/AiModelsCatalogClient.js";
import {
  modelVendorRefFromIdentity,
  modelVendorRefFromVendorAndSlug,
  resolveModelVendorFromResolution,
  resolveModelVendorLastResort,
  type ModelVendorRef,
  type ResolveModelVendorOptions,
} from "../sync/modelNameResolver/resolveModelVendor.js";
import { matchModelInAiProfiles } from "./aiProfilesMatch.js";
import { buildCatalogResolveAttempts, resolveFromCatalogAttempts } from "./costModelResolution.js";

export type { ModelVendorRef, ResolveModelVendorOptions };
export {
  resolveModelVendorFromResolution,
  resolveModelVendorSync,
  resolveModelVendorLastResort,
  modelVendorRefFromIdentity,
  modelVendorRefFromVendorAndSlug,
} from "../sync/modelNameResolver/resolveModelVendor.js";

/** Best-effort vendor lookup from a model name alone (catalog + ai-profiles). */
export async function resolveModelVendor(
  model: string,
  catalog: AiModelsCatalogClient,
  options?: ResolveModelVendorOptions,
): Promise<ModelVendorRef | null> {
  const exact = modelVendorRefFromIdentity(model, options);
  if (exact) return exact;

  const attempts = buildCatalogResolveAttempts(model);
  const result = await resolveFromCatalogAttempts(catalog, attempts, options);

  if (result?.found) {
    const ref = resolveModelVendorFromResolution(result, model, options);
    if (ref) return ref;
  }

  const profileMatch = await matchModelInAiProfiles(model, undefined, {
    catalogLane: options?.catalogLane,
  });
  if (profileMatch) {
    return modelVendorRefFromVendorAndSlug(
      profileMatch.provider,
      profileMatch.modelId,
      options,
    );
  }

  if (!result?.found && result?.bestRejectedCandidate) {
    const ref = resolveModelVendorFromResolution(result, model, options);
    if (ref) return ref;
  }

  return resolveModelVendorLastResort(model, options);
}
